import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Sparkle } from "lucide-react";
import { DOLLY_QUOTES, ETSY_SHOP } from "../data/site";
import { useEtsyListings, isDollyListing, tributeItem } from "../data/etsy";
import { ProductCard } from "./ProductCard";
import { Reveal } from "./motion";

export default function TributePage() {
  const listings = useEtsyListings();
  const dollyItems = listings.filter(isDollyListing).map(tributeItem);

  return (
    <main data-testid="tribute-page">
      <div className="border-b border-cream/10 bg-navy text-cream">
        <div className="mx-auto max-w-[1600px] px-6 pt-36 pb-16 md:px-12 md:pt-44 md:pb-24">
          <Reveal>
            <Link
              to="/"
              data-testid="tribute-back-link"
              className="group inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] text-cream/60 transition-colors duration-300 hover:text-cream"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              Home
            </Link>
            <p className="mt-10 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.35em] text-cream/50">
              <Sparkle className="h-4 w-4 fill-mustard text-mustard" />
              In loving memory · 1946 — 2026
            </p>
            <h1 className="mt-8 max-w-5xl font-playfair text-5xl leading-[0.92] tracking-tight md:text-7xl lg:text-8xl">
              Her words, kept close.
            </h1>
            <p className="mt-8 max-w-xl font-playfair text-xl italic leading-relaxed text-cream/70 md:text-2xl">
              “The way I see it, if you want the rainbow, you gotta put up with the rain.”
            </p>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-cream/60 md:text-base">
              Every piece below carries one of Dolly's quotes — lettered in the studio, listed on Etsy, and synced
              here the moment it goes live.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-24">
        {dollyItems.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {dollyItems.map((item, i) => (
              <Reveal key={`${item.name}-${i}`} delay={Math.min((i % 4) * 0.06, 0.24)} className="h-full">
                <ProductCard item={item} setId="tribute" />
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal>
            <div data-testid="tribute-empty-state" className="border border-navy/25 bg-blush/50 px-8 py-14 md:px-12">
              <p className="font-caveat text-3xl font-semibold text-navy/80">being lettered now…</p>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-navy/70 md:text-base">
                The first pieces carrying Dolly's quotes are being made in the studio. Follow the Etsy shop and they
                will appear right here — with photos and prices — the moment they list.
              </p>
              <a
                href={ETSY_SHOP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tribute-follow-etsy-btn"
                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-navy px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
              >
                Follow the shop on Etsy
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </Reveal>
        )}

        <div className="mt-20 border-t border-navy/15 pt-14">
          <Reveal>
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-navy/50">More of her light</p>
          </Reveal>
          <div className="mt-6 grid gap-x-16 md:grid-cols-2">
            {DOLLY_QUOTES.slice(1, 7).map((q, i) => (
              <Reveal key={q} delay={Math.min(i * 0.05, 0.25)}>
                <blockquote
                  data-testid={`tribute-quote-${i + 1}`}
                  className="border-t border-navy/10 py-6 font-playfair text-xl italic leading-snug text-navy md:text-2xl"
                >
                  “{q}”
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
